import { useState, useEffect } from 'react';
import { EventS } from '../../../Service/EventS';
import BookingEvent, { BookingEventDateItem, EventsFilter } from '../../../Types/Event';
import { useJwtClaims } from '../../../Service/Context/AuthContext';
import { useParams } from 'react-router-dom';
import { Stack } from '@mantine/core';
import { EventItemV2 } from '../../../Molecules/Eventlist/EventItem';
import DatelessEventItem from '../../../Molecules/Eventlist/DatelessEventItem';
import SkeletonItems from '../../../Atoms/SkeletonItems';
import EmptyText from '../../../Atoms/EmptyText';
import EventsSortedMonthly from '../../../Organismns/Eventlist/EvensSortedMonthly';

const ContactEvents = () => {
  const { profileId } = useJwtClaims();
  const { id: contactId } = useParams();
  const [loading, setLoading] = useState(false);
  const [bookingEventsDates, setBookingEventsDates] = useState<BookingEventDateItem[]>([]);

  const [filter, setFilter] = useState<EventsFilter>({
    page: 0,
    interval: 'ALL_TIME',
    sorting: 'chronological',
    answersOfInterest: ['AVAILABLE', 'BLOCKED'],
    profiles: [profileId],
    showAllEvents: false,
    agencyModeActive: false,
    filterCustomerContactShared: false,
    statesOfInterest: ['OFFERED', 'OPEN', 'BOOKED', 'CANCELED', 'UNAVAILABLE'],
    customStatesOfInterest: [],
    kindsOfInterest: [],
    customersOfInterest: [],
    recommendationBookingStates: ['OFFERED', 'OPEN', 'BOOKED'],
    showAppointments: true,
    filterChanged: false,
    applicationUsersOfInterests: [],
  });

  useEffect(() => {
    if (contactId) {
      setLoading(true);
      EventS.fetchEventsV2({ ...filter, customersOfInterest: [contactId] }, () => null)
        .then((resJSON) => {
          setBookingEventsDates(resJSON.reverse());
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  if (loading) {
    return <SkeletonItems height={50} />;
  }

  if (bookingEventsDates.length === 0) {
    return <EmptyText mt="xl" />;
  }

  return <EventsSortedMonthly eventDates={bookingEventsDates} />;
};

export const ContactDatelessEvents = () => {
  const { profileId } = useJwtClaims();
  const { id: contactId } = useParams();
  const [loading, setLoading] = useState(false);
  const [bookingEventsDates, setBookingEventsDates] = useState<BookingEvent[]>([]);

  const [filter] = useState<EventsFilter>({
    page: 0,
    interval: 'ALL_TIME',
    sorting: 'chronological',
    answersOfInterest: ['AVAILABLE', 'BLOCKED'],
    profiles: [profileId],
    showAllEvents: false,
    agencyModeActive: false,
    filterCustomerContactShared: false,
    statesOfInterest: ['OFFERED', 'OPEN', 'BOOKED', 'CANCELED', 'UNAVAILABLE'],
    customStatesOfInterest: [],
    kindsOfInterest: [],
    customersOfInterest: [],
    recommendationBookingStates: ['OFFERED', 'OPEN', 'BOOKED'],
    showAppointments: true,
    filterChanged: false,
    applicationUsersOfInterests: [],
  });

  useEffect(() => {
    if (contactId) {
      setLoading(true);
      EventS.fetchDatelessEvents({ ...filter, customersOfInterest: [contactId] })
        .then(setBookingEventsDates)
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  if (loading) {
    return <SkeletonItems height={50} />;
  }

  if (bookingEventsDates.length === 0) {
    return <EmptyText mt="xl" />;
  }

  return (
    <Stack>
      {bookingEventsDates.map((d) => (
        <DatelessEventItem key={d.id} bookingEvent={d} />
      ))}
    </Stack>
  );
};

export const ContactRecommendationEvents = () => {
  const { profileId } = useJwtClaims();
  const { id: contactId } = useParams();
  const [loading, setLoading] = useState(false);
  const [bookingEventsDates, setBookingEventsDates] = useState<BookingEventDateItem[]>([]);

  const [filter] = useState<EventsFilter>({
    page: 0,
    interval: 'ALL_TIME',
    sorting: 'chronological',
    answersOfInterest: ['AVAILABLE', 'BLOCKED', 'UNAVAILABLE', 'UNFIT', 'UNKNOWN'],
    profiles: [profileId],
    showAllEvents: false,
    agencyModeActive: false,
    filterCustomerContactShared: false,
    statesOfInterest: ['OFFERED', 'OPEN', 'BOOKED', 'CANCELED', 'UNAVAILABLE'],
    customStatesOfInterest: [],
    kindsOfInterest: [],
    customersOfInterest: [],
    recommendationBookingStates: ['OFFERED', 'OPEN', 'BOOKED', 'CANCELED', 'UNAVAILABLE'],
    showAppointments: true,
    filterChanged: false,
    applicationUsersOfInterests: [],
  });

  useEffect(() => {
    if (contactId) {
      setLoading(true);
      EventS.fetchRecommendationsV3({ ...filter, recommendationContacts: [contactId] }, () => {})
        .then((resJSON) => {
          setBookingEventsDates(resJSON.reverse());
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  if (loading) {
    return <SkeletonItems height={50} />;
  }

  if (bookingEventsDates.length === 0) {
    return <EmptyText mt="xl" />;
  }

  return <EventsSortedMonthly eventDates={bookingEventsDates} />;
};

export default ContactEvents;
